const ERROR_QUEUE_KEY = 'agroel:client-error-queue:v1'
const INSTALL_ID_KEY = 'agroel_export_client_install_id'
const SESSION_ID_KEY = 'agroel_export_client_session_id'
const MAX_QUEUE_SIZE = 50
const MAX_OCCURRENCES = 100
const QUEUE_TTL_MS = 7 * 24 * 60 * 60 * 1000
const SENSITIVE_KEY_RE = /authorization|cookie|csrf|password|passwd|secret|token|api[_-]?key|session[_-]?id|client[_-]?install[_-]?id|installation[_-]?id/i
const TOKEN_RE = /(?:bearer\s+)?eyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}/gi
const LEVEL_PRIORITY = Object.freeze({ fatal: 3, error: 2, warning: 1 })

let configuredClientProfile = 'web'

function rootScope () {
  if (typeof window !== 'undefined') return window
  if (typeof self !== 'undefined') return self
  return null
}

function storageFor (kind = 'local') {
  const root = rootScope()
  try {
    return kind === 'session' ? root?.sessionStorage : root?.localStorage
  } catch (error) {
    void error
    return null
  }
}

function cleanText (value, limit = 4000) {
  let text = String(value == null ? '' : value).trim()
  text = text.replace(TOKEN_RE, '[REDACTED_TOKEN]')
  return text.length > limit ? text.slice(0, limit) : text
}

function safeUrlPath (value, limit = 500) {
  const root = rootScope()
  try {
    const url = new URL(String(value || ''), root?.location?.origin || 'https://agroel.invalid')
    return cleanText(url.pathname || '/', limit)
  } catch (error) {
    void error
    return cleanText(String(value || '').split('?')[0].split('#')[0], limit)
  }
}

function numericRouteContext (value) {
  const out = {}
  const root = rootScope()
  try {
    const url = new URL(String(value || ''), root?.location?.origin || 'https://agroel.invalid')
    for (const [source, target] of [
      ['device', 'device_id'], ['device_id', 'device_id'],
      ['storage', 'storage_id'], ['storage_id', 'storage_id'],
      ['page', 'page_id'], ['page_id', 'page_id']
    ]) {
      const raw = String(url.searchParams.get(source) || '').trim()
      if (/^\d+$/.test(raw)) out[target] = Number(raw)
    }
  } catch (error) {
    void error
  }
  return out
}

function sanitizeContext (value, depth = 0, key = '') {
  if (SENSITIVE_KEY_RE.test(String(key || ''))) return '[REDACTED]'
  if (depth >= 4) return '[TRUNCATED_DEPTH]'
  if (value == null || typeof value === 'boolean' || typeof value === 'number') return value
  if (typeof value === 'string') {
    return /url|uri|route|path|location|asset/i.test(String(key || ''))
      ? safeUrlPath(value, 1000)
      : cleanText(value, 2000)
  }
  if (Array.isArray(value)) return value.slice(0, 50).map(item => sanitizeContext(item, depth + 1))
  if (typeof value === 'object') {
    const out = {}
    Object.entries(value).slice(0, 50).forEach(([rawKey, rawValue]) => {
      const safeKey = cleanText(rawKey, 120)
      if (safeKey) out[safeKey] = sanitizeContext(rawValue, depth + 1, safeKey)
    })
    return out
  }
  return cleanText(value, 2000)
}

function randomId () {
  const cryptoApi = rootScope()?.crypto
  if (typeof cryptoApi?.randomUUID === 'function') return cryptoApi.randomUUID()
  if (typeof cryptoApi?.getRandomValues === 'function') {
    const bytes = new Uint8Array(16)
    cryptoApi.getRandomValues(bytes)
    return Array.from(bytes).map(value => value.toString(16).padStart(2, '0')).join('')
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}-${Math.random().toString(16).slice(2)}`
}

function stableClientId (storage, key) {
  if (!storage) return ''
  try {
    let value = cleanText(storage.getItem(key), 200)
    if (!value) {
      value = randomId()
      storage.setItem(key, value)
    }
    return value
  } catch (error) {
    void error
    return ''
  }
}

function releaseMetadata () {
  const root = rootScope()
  const value = root?.__AGROEL_RELEASE_META__ || root?.__AGROEL_BUNDLE_META__ || {}
  return {
    release_version: cleanText(value.release_version || value.version || '', 128),
    deployment_id: cleanText(value.deployment_id || '', 160),
    content_hash: cleanText(value.content_hash || '', 128),
    pwa_protocol_epoch: Number(value.pwa_protocol_epoch || 0) || null,
    dexie_schema_version: Number(value.dexie_schema_version || 0) || null
  }
}

function normalizeLevel (value) {
  const level = String(value || 'error').trim().toLowerCase()
  return Object.prototype.hasOwnProperty.call(LEVEL_PRIORITY, level) ? level : 'error'
}

function errorValue (error, key, fallback = '') {
  if (error && typeof error === 'object' && error[key] != null) return error[key]
  return fallback
}

function normalizedFingerprintText (value) {
  return cleanText(value, 1000)
    .toLowerCase()
    .replace(/[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}/gi, 'uuid')
    .replace(/:\d+(?::\d+)?(?=\)?(?:\s|$))/g, ':line')
    .replace(/\b\d{3,}\b/g, 'n')
    .replace(/\s+/g, ' ')
}

function queueFingerprint (event) {
  return [
    event.event_kind,
    event.exception_class,
    event.route_name || event.route_path,
    event.message,
    String(event.stack || '').split('\n').slice(0, 5).join('|')
  ].map(normalizedFingerprintText).join('::')
}

function parseStoredQueue () {
  const storage = storageFor('local')
  if (!storage) return []
  try {
    const parsed = JSON.parse(storage.getItem(ERROR_QUEUE_KEY) || '[]')
    return Array.isArray(parsed) ? parsed.filter(row => row && typeof row === 'object') : []
  } catch (error) {
    void error
    return []
  }
}

function eventTimestamp (event, key = 'last_occurred_at') {
  const value = Date.parse(event?.[key] || event?.occurred_at || '')
  return Number.isFinite(value) ? value : 0
}

function normalizeQueue (rows, now = Date.now()) {
  const oldest = now - QUEUE_TTL_MS
  return rows
    .filter(row => eventTimestamp(row) >= oldest)
    .sort((left, right) => {
      const priority = (LEVEL_PRIORITY[right.level] || 0) - (LEVEL_PRIORITY[left.level] || 0)
      if (priority) return priority
      return eventTimestamp(right) - eventTimestamp(left)
    })
    .slice(0, MAX_QUEUE_SIZE)
    .sort((left, right) => eventTimestamp(left, 'occurred_at') - eventTimestamp(right, 'occurred_at'))
}

function writeQueue (rows) {
  const storage = storageFor('local')
  if (!storage) return false
  try {
    storage.setItem(ERROR_QUEUE_KEY, JSON.stringify(normalizeQueue(rows)))
    return true
  } catch (error) {
    void error
    return false
  }
}

export function configureClientErrorTelemetry ({ clientProfile = '' } = {}) {
  configuredClientProfile = cleanText(clientProfile, 32).toLowerCase() || configuredClientProfile
}

export function readClientErrorQueue () {
  const normalized = normalizeQueue(parseStoredQueue())
  writeQueue(normalized)
  return normalized
}

export function buildClientErrorEvent (error, context = {}) {
  const root = rootScope()
  const currentUrl = context.url || root?.location?.href || ''
  const release = releaseMetadata()
  const occurredAt = cleanText(context.occurred_at || new Date().toISOString(), 64)
  const eventKind = cleanText(context.event_kind || context.source_event || 'frontend.unhandled', 96)
  const routePath = safeUrlPath(context.route_path || currentUrl, 500)
  const safeContext = sanitizeContext({
    ...context,
    ...numericRouteContext(context.route_path || currentUrl),
    online: root?.navigator?.onLine !== false,
    standalone: !!(
      root?.navigator?.standalone ||
      root?.matchMedia?.('(display-mode: standalone)')?.matches
    )
  })
  delete safeContext.event_kind
  delete safeContext.source_event
  delete safeContext.level
  delete safeContext.route_path
  delete safeContext.route_name
  delete safeContext.url
  delete safeContext.occurred_at

  return {
    event_id: cleanText(context.event_id || randomId(), 200),
    level: normalizeLevel(context.level),
    event_kind: eventKind || 'frontend.unhandled',
    occurred_at: occurredAt,
    last_occurred_at: occurredAt,
    occurrence_count: 1,
    exception_class: cleanText(errorValue(error, 'name', error?.constructor?.name || 'FrontendError'), 255) || 'FrontendError',
    message: cleanText(
      typeof error === 'string' ? error : errorValue(error, 'message', errorValue(error, 'reason', error || 'Frontend error')),
      4000
    ) || 'Frontend error',
    stack: cleanText(errorValue(error, 'stack', error?.reason?.stack || ''), 16000),
    route_path: routePath,
    route_name: cleanText(context.route_name || '', 255),
    component_info: cleanText(context.component_info || '', 500),
    user_agent: cleanText(root?.navigator?.userAgent || context.user_agent || '', 500),
    client_profile: cleanText(context.client_profile || configuredClientProfile, 32).toLowerCase(),
    client_install_id: stableClientId(storageFor('local'), INSTALL_ID_KEY),
    client_session_id: stableClientId(storageFor('session'), SESSION_ID_KEY),
    release_version: release.release_version,
    release,
    context: safeContext,
    transport_attempts: 0,
    next_attempt_at: 0
  }
}

export function enqueueClientErrorEvent (event) {
  if (!event || typeof event !== 'object') return null
  const rows = readClientErrorQueue()
  const fingerprint = queueFingerprint(event)
  const duplicate = rows.find(row => queueFingerprint(row) === fingerprint)
  if (duplicate) {
    duplicate.occurrence_count = Math.min(MAX_OCCURRENCES, Math.max(1, Number(duplicate.occurrence_count || 1)) + 1)
    duplicate.last_occurred_at = cleanText(event.last_occurred_at || event.occurred_at || new Date().toISOString(), 64)
    if ((LEVEL_PRIORITY[event.level] || 0) > (LEVEL_PRIORITY[duplicate.level] || 0)) duplicate.level = event.level
    duplicate.transport_attempts = 0
    duplicate.next_attempt_at = 0
    writeQueue(rows)
    return duplicate
  }
  rows.push(event)
  writeQueue(rows)
  return event
}

export function captureClientError (error, context = {}) {
  return enqueueClientErrorEvent(buildClientErrorEvent(error, context))
}

export function removeClientErrorEvents (eventIds = []) {
  const ids = new Set((Array.isArray(eventIds) ? eventIds : []).map(value => String(value || '')))
  if (!ids.size) return
  writeQueue(readClientErrorQueue().filter(row => !ids.has(String(row.event_id || ''))))
}

export function acknowledgeClientErrorEvents (sentEvents = []) {
  const sentById = new Map(
    (Array.isArray(sentEvents) ? sentEvents : [])
      .filter(event => event && event.event_id)
      .map(event => [String(event.event_id), event])
  )
  if (!sentById.size) return
  const rows = []
  for (const current of readClientErrorQueue()) {
    const sent = sentById.get(String(current.event_id || ''))
    if (!sent) {
      rows.push(current)
      continue
    }
    const residualCount = Math.max(0, Number(current.occurrence_count || 1) - Number(sent.occurrence_count || 1))
    if (!residualCount) continue
    rows.push({
      ...current,
      event_id: randomId(),
      occurred_at: current.last_occurred_at || new Date().toISOString(),
      occurrence_count: Math.min(MAX_OCCURRENCES, residualCount),
      transport_attempts: 0,
      next_attempt_at: 0
    })
  }
  writeQueue(rows)
}

export function deferClientErrorEvents (eventIds = [], { delayMs = 0, increment = true } = {}) {
  const ids = new Set((Array.isArray(eventIds) ? eventIds : []).map(value => String(value || '')))
  const nextAttemptAt = Date.now() + Math.max(0, Number(delayMs || 0))
  const rows = readClientErrorQueue().map(row => {
    if (!ids.has(String(row.event_id || ''))) return row
    return {
      ...row,
      transport_attempts: increment
        ? Math.min(6, Math.max(0, Number(row.transport_attempts || 0)) + 1)
        : Math.max(0, Number(row.transport_attempts || 0)),
      next_attempt_at: nextAttemptAt
    }
  })
  writeQueue(rows)
}

export function installPreBootstrapClientErrorReporting () {
  const root = rootScope()
  if (!root?.addEventListener) return null
  if (root.__AGROEL_EARLY_ERROR_REPORTER__) return root.__AGROEL_EARLY_ERROR_REPORTER__
  const onError = event => {
    const target = event?.target
    const asset = target && target !== root ? (target.src || target.href || '') : ''
    captureClientError(event?.error || event?.message || `Resource failed: ${safeUrlPath(asset)}`, {
      level: asset ? 'error' : 'fatal',
      event_kind: asset ? 'resource.load' : 'window.error',
      asset_url: asset,
      filename: event?.filename || '',
      lineno: Number(event?.lineno || 0) || null,
      colno: Number(event?.colno || 0) || null
    })
  }
  const onRejection = event => captureClientError(event?.reason || 'Unhandled promise rejection', {
    level: 'fatal',
    event_kind: 'window.unhandledrejection'
  })
  root.addEventListener('error', onError, true)
  root.addEventListener('unhandledrejection', onRejection)
  const reporter = {
    capture: captureClientError,
    detach () {
      root.removeEventListener('error', onError, true)
      root.removeEventListener('unhandledrejection', onRejection)
      if (root.__AGROEL_EARLY_ERROR_REPORTER__ === reporter) delete root.__AGROEL_EARLY_ERROR_REPORTER__
    }
  }
  root.__AGROEL_EARLY_ERROR_REPORTER__ = reporter
  return reporter
}

export function detachPreBootstrapClientErrorReporting () {
  try {
    rootScope()?.__AGROEL_EARLY_ERROR_REPORTER__?.detach?.()
  } catch (error) {
    void error
  }
}

export {
  ERROR_QUEUE_KEY,
  MAX_QUEUE_SIZE,
  QUEUE_TTL_MS,
  sanitizeContext as sanitizeClientErrorContext,
  safeUrlPath as safeClientErrorRoute
}
